import type {
  RelatedPublicTrip,
  RelatedPublicSegment,
  PublicTrip,
  PublicTripLeg,
  PublicSegment,
  RelatedPublicTripV4,
  PublicTripV4,
  RelatedPublicSegmentV4,
  PublicSegmentV4
} from "@busbud/core-types/dist/lib/poll-service/api";

import type { AmplitudeEvent } from "@app/types/tracking/common";
import type {
  ClickedStopDetailsLink,
  ClickedStopMapLink,
  CollapseStops,
  ExpandStops,
  ShowedReturnItinerary,
  SwitchItineraryTabs
} from "@app/types/tracking/itinerary";

import { ItineraryTrackingAction } from "./constants/tracking-action";
import { isInterconnection } from "../helpers/udm/trip";
import { getTripStopsStats as getUdmTripStopsStats } from "../helpers/udm/trip";

export function expandStops(
  itinerary_direction: string
): AmplitudeEvent<ExpandStops> {
  return {
    action: ItineraryTrackingAction.ExpandStops,
    details: { itinerary_direction }
  };
}

export function collapseStops(
  itinerary_direction: string
): AmplitudeEvent<CollapseStops> {
  return {
    action: ItineraryTrackingAction.CollapseStops,
    details: { itinerary_direction }
  };
}

export function switchItineraryTabs(
  itinerary_direction: string
): AmplitudeEvent<SwitchItineraryTabs> {
  return {
    action: ItineraryTrackingAction.SwitchItineraryTabs,
    details: { itinerary_direction }
  };
}

interface ShowedItineraryProperties {
  with_transfer: boolean | null;
  is_interconnected: boolean;
  transfer_time: number;
  transfer_count: number;
  stop_time: number;
  stop_count: number;
}

export type ShowedUdmItineraryParams = {
  trip: RelatedPublicTrip | PublicTrip | RelatedPublicTripV4 | PublicTripV4;
  segments: (
    | RelatedPublicSegment
    | PublicSegment
    | RelatedPublicSegmentV4
    | PublicSegmentV4
  )[];
  trip_leg: PublicTripLeg;
};
export function showedUdmItinerary({
  trip,
  segments,
  trip_leg
}: ShowedUdmItineraryParams) {
  try {
    const segments_by_id = segments.reduce(
      (
        acc:
          | Record<string, RelatedPublicSegment>
          | Record<string, PublicSegment>
          | Record<string, RelatedPublicSegmentV4>
          | Record<string, PublicSegmentV4>,
        cur:
          | RelatedPublicSegment
          | PublicSegment
          | RelatedPublicSegmentV4
          | PublicSegmentV4
      ) => {
        acc[cur.id] = cur;
        return acc;
      },
      {}
    );

    const trip_stops_stats = getUdmTripStopsStats(segments_by_id, trip_leg);

    const properties: ShowedItineraryProperties = {
      with_transfer: trip_leg.segment_ids.length > 1,
      is_interconnected: isInterconnection(trip, trip_leg),
      transfer_time: trip_stops_stats.transfers.total_duration,
      transfer_count: trip_stops_stats.transfers.count,
      stop_time: trip_stops_stats.stops.total_duration,
      stop_count: trip_stops_stats.stops.count
    };

    return showedReturnItinerary(properties);
  } catch {
    return null;
  }
}

function showedReturnItinerary(
  properties: ShowedItineraryProperties
): AmplitudeEvent<ShowedReturnItinerary> {
  return {
    action: ItineraryTrackingAction.ShowedReturnItinerary,
    details: {
      selection_return_with_transfer: properties.with_transfer,
      selection_return_is_interconnected: properties.is_interconnected,
      selection_return_transfer_time: properties.transfer_time,
      selection_return_stop_time: properties.stop_time,
      selection_return_transfer_count: properties.transfer_count,
      selection_return_stop_count: properties.stop_count
    }
  };
}

export function clickedStopDetailsLink(
  properties: ClickedStopDetailsLink
): AmplitudeEvent<ClickedStopDetailsLink> {
  return {
    action: ItineraryTrackingAction.ClickedStopDetailsLink,
    details: {
      stop_details_link: properties.stop_details_link
    }
  };
}

export function clickedStopMapLink(properties: ClickedStopMapLink) {
  return {
    action: ItineraryTrackingAction.ClickedStopMapLink,
    details: {
      stop_details_name: properties.stop_details_name
    }
  };
}
